/**
 * tokens.ts · TypeScript mirror of docs/design.md §1–4
 * Consumers reference tokens through this module so magic strings never
 * leak into components. Any drift from design.md is a bug — grep for
 * `tokens.ts` before editing docs/design.md.
 */
// ---------------------------------------------------------------
// Colors (docs/design.md §1)
// ---------------------------------------------------------------
export const colors = {
    deepInk: "#000d10",
    pureWhite: "#ffffff",
    coolAsh: "#8e8e95",
    pebble: "#d5d3d4",
    midnightHull: "#0f0f1c",
    charcoalDeck: "#151623",
    clayEmber: "#bc7155",
};
// Bunching-specific mapping (docs/design.md §1 second table)
export const semanticColors = {
    activeAlertBg: colors.clayEmber,
    baselineSeries: colors.deepInk,
    mpcSeries: colors.clayEmber,
    targetHeadway: colors.coolAsh,
    vehicleDefault: colors.deepInk,
    vehicleBunchedStroke: colors.clayEmber,
    vehicleBunchedFill: colors.deepInk,
    vehicleHeldStroke: colors.coolAsh,
    simulatedPillBg: colors.coolAsh,
};
// ---------------------------------------------------------------
// Typography (docs/design.md §2)
// ---------------------------------------------------------------
export const typography = {
    fontFamily: '"HelveticaNowDisplay", ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    weights: {
        regular: 400,
        bold: 700,
    },
    sizes: {
        caption: 17,
        nav: 20,
        subheading: 23,
        headingSm: 30,
        heading: 37,
        headingLg: 52,
        display: 63,
        displayXl: 131,
        hero: 187,
        body: 18,
    },
    lineHeightBody: 29,
    tracking: {
        subheading: "-0.23px",
        heading: "-0.37px",
        headingLg: "-0.52px",
        display: "-1.26px",
        displayXl: "-2.62px",
        hero: "-3.74px",
    },
};
// ---------------------------------------------------------------
// Spacing scale (docs/design.md §3)
// ---------------------------------------------------------------
export const spacing = {
    s11: 11,
    s13: 13,
    s15: 15,
    s16: 16,
    s17: 17,
    s21: 21,
    s22: 22,
    s23: 23,
    s31: 31,
    s34: 34,
    s38: 38,
    s52: 52,
    s53: 53,
    s59: 59,
    s68: 68,
    s119: 119,
};
export const layout = {
    pageMaxWidth: 1200,
    sectionGap: 80,
    cardPadding: 22,
    elementGap: 16,
};
// ---------------------------------------------------------------
// Radius (docs/design.md §4)
// ---------------------------------------------------------------
export const radius = {
    nav: 1000,
    button: 1000,
    decorative: 45,
    hero: 0,
    icon: "100%",
};
export const AS_4_STOPS = [
    { stopId: "AS-4-S01", name: "Andheri Station East", lat: 19.1197, lng: 72.8464 },
    { stopId: "AS-4-S02", name: "Marol Naka", lat: 19.1155, lng: 72.8555 },
    { stopId: "AS-4-S03", name: "MIDC", lat: 19.1132, lng: 72.8608 },
    { stopId: "AS-4-S04", name: "JB Nagar", lat: 19.1108, lng: 72.8681 },
    { stopId: "AS-4-S05", name: "Chakala", lat: 19.1090, lng: 72.8712 },
    { stopId: "AS-4-S06", name: "SEEPZ West Gate", lat: 19.1055, lng: 72.8746 },
    { stopId: "AS-4-S07", name: "SEEPZ", lat: 19.1103, lng: 72.8798 },
];
export const MUMBAI_CENTER = [19.1123, 72.8626];
/** Phase 12 — Delhi centroid, used when the OTD live feed is active. */
export const DELHI_CENTER = [28.6139, 77.209];
/**
 * Phase 13a — MMR corridor centroid. Fits Panvel, Kharghar, Belapur, Vashi,
 * Airoli, Thane, Kalyan, Dombivli in a single ~zoom-11 viewport.
 */
export const MUMBAI_CORRIDOR_CENTER = [19.05, 73.05];
export const AS_4_ROUTE_ID = "AS-4";
/**
 * Phase 13a — mumbai-mode route_id prefixes. When we see any of these on
 * `envelope.entities`, we're in Mumbai-corridor mode and re-center the map.
 * NMMT_ added in Phase 13b for Navi Mumbai routes (Panvel/Karjat/Airport).
 */
export const MUMBAI_ROUTE_PREFIXES = [
    "KDMT_",
    "TMT_",
    "BEST_",
    "VVMT_",
    "NMMT_",
];
/** Returns true when the envelope carries schedule-driven Mumbai corridor buses. */
export function isMumbaiCorridorFeed(entities) {
    if (entities.length === 0)
        return false;
    return entities.some((e) => MUMBAI_ROUTE_PREFIXES.some((p) => e.trip.route_id.startsWith(p)));
}
